'use client'

import * as React from 'react'

import { ExternalLinkIcon, ReloadIcon } from '@radix-ui/react-icons'
import Link from 'next/link'
import { useSignOut } from '~/app/_store/user-store'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../sotano-kit/primitives/dropdown-menu'
import { BaseHubAvatar } from '../basehub-avatar'
import { homepageRoute } from '../../constants/routing'
import { socialLinks, xGlyph } from '@basehub/shared-constants'
import { Actions, ActionsProps } from '../mkt/header/actions'
import { useHref__CLIENT_ONLY } from '../../hooks/use-href'
import { clsx } from 'clsx'
import { useHasRendered } from '~/hooks/use-has-rendered'
import { ThemeSwitcher } from './theme-switcher'
import { PublicUserData, useUserData } from '~/hooks-data/use-user-data'
import { dropAllDatabases } from 'replicache'
import { toast } from 'sonner'

type UserAvatarDropdownProps = {
  isSignedIn?: boolean
  notFoundActionProps?: ActionsProps & { hide?: boolean }
  themeSwitcher: boolean
}

export const UserAvatarDropdown = ({
  isSignedIn: prefetchedIsSignedIn,
  notFoundActionProps,
  themeSwitcher,
}: UserAvatarDropdownProps) => {
  const { data: userData } = useUserData(null)
  const next = useHref__CLIENT_ONLY()
  const hasRendered = useHasRendered()
  if (!prefetchedIsSignedIn && !userData) {
    if (notFoundActionProps?.hide || !hasRendered) return null
    return <Actions loginNext={next} {...notFoundActionProps} />
  }

  if (!userData) return null

  if (userData.isPlayground) {
    return (
      <PlaygroundAvatarDropdown
        userData={userData}
        themeSwitcher={themeSwitcher}
      />
    )
  }

  return (
    <LoggedInAvatarDropdown userData={userData} themeSwitcher={themeSwitcher} />
  )
}

const LoggedInAvatarDropdown: React.FC<{
  userData: PublicUserData
  themeSwitcher: boolean
}> = ({ userData, themeSwitcher }) => {
  const signOut = useSignOut()
  const accountSlug = userData.username ?? 'dashboard'

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={clsx(
          'relative size-navbar-element gap-x-[5px] bg-dark-shade flex overflow-hidden rounded-[100px] border transition-colors border-dark-border-solid outline-1 outline-offset-2 focus:outline focus:outline-dark-control-base group hover:border-dark-elevated cursor-default hover:bg-dark-shade-hover'
        )}
        id="user-avatar-dropdown-trigger"
      >
        <BaseHubAvatar
          size="fill"
          id={userData.id}
          name={userData.username ?? userData.id}
          forceSrc={userData.imageUrl ?? undefined}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="!max-w-[180px]">
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link href={`/${accountSlug}`}>
              <span>Dashboard</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              href={`/${accountSlug}/settings`}
              className="space-x-2 text-xs leading-none"
            >
              <span>Account Settings</span>
            </Link>
          </DropdownMenuItem>
          {themeSwitcher && <ThemeSwitcher />}
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link
              className="inline-flex group w-full justify-between space-x-2 text-xs leading-none"
              href="https://docs.basehub.com"
              target="_blank"
              rel="noopener"
            >
              <span>Documentation</span>
              <ExternalLinkIcon className="group-hover:block hidden" />
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              className="inline-flex group w-full justify-between space-x-2 text-xs leading-none"
              href="https://help.basehub.com"
              target="_blank"
              rel="noopener"
            >
              <span>Help Center</span>
              <ExternalLinkIcon className="group-hover:block hidden" />
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              className="inline-flex group w-full justify-between space-x-2 text-xs leading-none"
              href="/changelog"
              target="_blank"
              rel="noopener"
            >
              <span>Changelog</span>
              <ExternalLinkIcon className="group-hover:block hidden" />
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link
              href={homepageRoute}
              className="inline-flex group w-full justify-between space-x-2 text-xs leading-none"
              target="_blank"
              rel="noopener"
            >
              <span>Homepage</span>
              <ExternalLinkIcon className="group-hover:block hidden" />
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              href={socialLinks.x}
              className="inline-flex group w-full justify-between space-x-2 text-xs leading-none"
              target="_blank"
              rel="noopener"
            >
              <span>{xGlyph}</span>
              <ExternalLinkIcon className="group-hover:block hidden" />
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem
            className="inline-flex group w-full justify-between space-x-2 text-xs leading-none"
            onSelect={async () => {
              const { errors } = await dropAllDatabases()
              if (errors.length > 0) {
                console.error('errors while dropping databases', errors)
                toast.error('Failed to drop databases')
                return
              }
              window.location.reload()
            }}
          >
            <span>Force Reload App</span>
            <ReloadIcon className="group-hover:block hidden" />
          </DropdownMenuItem>
          <DropdownMenuItem
            className="space-x-2 text-xs leading-none text-dark-error"
            onSelect={async () => {
              await signOut('/')
            }}
          >
            <span>Sign Out</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const PlaygroundAvatarDropdown: React.FC<{
  userData: PublicUserData
  themeSwitcher: boolean
}> = ({ userData }) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        id="user-avatar-dropdown-trigger"
        className={clsx(
          'relative size-navbar-element gap-x-[5px] bg-dark-shade flex overflow-hidden rounded-[100px] border transition-colors border-dark-border-solid outline-1 outline-offset-2 focus:outline focus:outline-dark-control-base group hover:border-dark-elevated cursor-default hover:bg-dark-shade-hover'
        )}
      >
        <BaseHubAvatar
          size="fill"
          id={userData.id}
          name={userData.username ?? userData.id}
          forceSrc={userData.imageUrl ?? undefined}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="!max-w-[180px]">
        <DropdownMenuGroup>
          <ThemeSwitcher />
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link
              className="inline-flex group w-full justify-between space-x-2 text-xs leading-none"
              href="https://docs.basehub.com"
              target="_blank"
              rel="noopener"
            >
              <span>Documentation</span>
              <ExternalLinkIcon className="group-hover:block hidden" />
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              className="inline-flex group w-full justify-between space-x-2 text-xs leading-none"
              href="https://help.basehub.com"
              target="_blank"
              rel="noopener"
            >
              <span>Help Center</span>
              <ExternalLinkIcon className="group-hover:block hidden" />
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              className="inline-flex group w-full justify-between space-x-2 text-xs leading-none"
              href="/changelog"
              target="_blank"
              rel="noopener"
            >
              <span>Changelog</span>
              <ExternalLinkIcon className="group-hover:block hidden" />
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link
              href={homepageRoute}
              className="inline-flex group w-full justify-between space-x-2 text-xs leading-none"
              target="_blank"
              rel="noopener"
            >
              <span>Homepage</span>
              <ExternalLinkIcon className="group-hover:block hidden" />
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link
              href={socialLinks.x}
              className="inline-flex group w-full justify-between space-x-2 text-xs leading-none"
              target="_blank"
              rel="noopener"
            >
              <span>{xGlyph}</span>
              <ExternalLinkIcon className="group-hover:block hidden" />
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
